import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Mail, Phone, FileText } from 'lucide-react';
import { uc } from './unifiedContactsApi';
import { formatPhone } from './shared/phone';

// Contact profile → Relationships tab (ticket 232; grill 186, ticket 188 D12):
// the other borrowers on this contact's loan files, the way Arive lists them.
// Read-only. A person links to their contact when the server could match one;
// each loan chip deep-links into the loan file.
export default function ContactRelationships({ contactId }) {
  const [items, setItems] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    setItems(null);
    setFailed(false);
    uc.relationships(contactId)
      .then((d) => { if (alive) setItems(d.items || []); })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [contactId]);

  if (failed) {
    return (
      <div className="bg-red-50 border border-red-100 rounded-2xl p-8 text-center text-sm text-red-700" data-testid="contact-relationships-error">
        Could not load relationships. Reload the page to try again.
      </div>
    );
  }
  if (items === null) {
    return (
      <div className="p-8 flex items-center justify-center text-slate-400" data-testid="contact-relationships-loading">
        <Loader2 className="animate-spin mr-2" size={18} /> Loading relationships...
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div className="bg-slate-50 border border-dashed border-slate-200 rounded-2xl p-12 text-center text-slate-400" data-testid="contact-relationships-empty">
        <p className="text-sm">No co-borrowers on this contact's loan files.</p>
        <p className="text-xs mt-1">People listed on the same loan application show up here.</p>
      </div>
    );
  }
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm divide-y divide-slate-100" data-testid="contact-relationships-list">
      {items.map((p, i) => (
        <div key={p.contact_id || i} className="px-4 py-3 flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4" data-testid={`relationship-${p.contact_id || i}`}>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              {p.contact_id ? (
                <Link to={`/portal/contacts/${p.contact_id}`} className="text-sm font-semibold hover:underline" style={{ color: '#070B5F' }}>
                  {p.name || p.email}
                </Link>
              ) : (
                <span className="text-sm font-semibold" style={{ color: '#070B5F' }}>{p.name || p.email}</span>
              )}
              <span className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 bg-slate-100 rounded px-1.5 py-0.5">{p.role}</span>
            </div>
            <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
              {p.email && <span className="inline-flex items-center gap-1 min-w-0"><Mail size={12} /> <span className="truncate">{p.email}</span></span>}
              {p.phone && <span className="inline-flex items-center gap-1"><Phone size={12} /> {formatPhone(p.phone)}</span>}
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5 sm:justify-end sm:max-w-[50%]">
            {p.loans.map((l) => (
              <Link
                key={l.loan_file_id}
                to={`/portal/loans/${l.loan_file_id}`}
                className="inline-flex items-center gap-1 text-[11px] font-medium rounded-full border border-slate-200 px-2 py-0.5 hover:bg-slate-50 transition text-slate-600 max-w-full"
              >
                <FileText size={11} className="shrink-0" /> <span className="truncate">{l.address || l.loan_number || 'Loan file'}</span>
              </Link>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
